import React from 'react';
import styled, { keyframes } from 'styled-components';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

/* Custom animations */
const float = keyframes`
  0%,
  100% {
    transform: translateY(0px);
  }
  50% {
    transform: translateY(-10px);
  }
`;

const pulseGlow = keyframes`
  0%,
  100% {
    box-shadow: 0 0 20px rgba(59, 130, 246, 0.3);
  }
  50% {
    box-shadow: 0 0 30px rgba(59, 130, 246, 0.6);
  }
`;

// 3D Tooth Container
const ToothContainer = styled.div`
  position: relative;
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  height: 100%;
`;

/* Add responsive visibility classes */
const GlobalStyle = styled.div`
  .desktop-only {
    display: block;
    
    @media (max-width: 988px) {
      display: none;
    }
  }
  
  .mobile-tablet-only {
    display: none;
    
    @media (max-width: 988px) {
      display: block;
    }
  }
`;

const AnimatedTooth = styled.div`
  animation: ${float} 3s ease-in-out infinite;
  
  .tooth-image {
    filter: drop-shadow(0px 15px 25px rgba(59, 130, 246, 0.4));
    width: 600px;
    height: 600px;
    max-width: 100%;
    height: auto;
  }
  
  @media (max-width: 992px) {
    .tooth-image {
      width: 400px;
      height: 400px;
    }
  }
  
  @media (max-width: 768px) {
    .tooth-image {
      width: 220px;
      height: 220px;
      max-width: 220px; /* Large size on mobile */
    }
  }
  
  @media (max-width: 480px) {
    .tooth-image {
      width: 180px;
      height: 180px;
      max-width: 180px; /* Medium-large size on very small screens */
    }
  }
`;

const HeroSection = styled.section`
  height: 100vh;
  max-height: 800px;
  width: 100%;
  background-color: #f0f6ff; /* Light blue background */
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;

  @media (max-width: 992px) {
    height: auto;
    padding: 80px 0 120px; /* Added bottom padding to make room for the button */
    position: relative; /* Ensure relative positioning for absolute children */
  }
  
  @media (max-width: 768px) {
    padding: 60px 0 100px; /* Maintain adequate bottom padding on mobile */
  }
`;

const HeroContent = styled.div`
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0 5%;
  position: relative;
  
  @media (max-width: 992px) {
    flex-direction: column;
    justify-content: center;
  }
  
  @media (max-width: 768px) {
    /* Special mobile layout that allows proper ordering */
    display: flex;
    flex-direction: column;
  }
`;

const TextContainer = styled.div`
  width: 50%;
  z-index: 2;
  display: flex;
  flex-direction: column;
  justify-content: center;

  @media (max-width: 992px) {
    width: 100%;
    order: 1; /* Ensure text is above image on mobile */
    text-align: center;
  }
  
  @media (max-width: 768px) {
    /* On mobile, we need to create a layout where elements can be reordered */
    display: flex;
    flex-direction: column;
  }
`;

const MainHeading = styled.h1`
  font-size: 5rem; /* Increased from 4rem */
  line-height: 1.2;
  margin-bottom: 1.5rem;
  font-weight: bold;

  .primary {
    color: #4169e1;
    display: block;
  }

  @media (max-width: 992px) {
    font-size: 4rem;
  }

  @media (max-width: 768px) {
    font-size: 3rem;
  }
  
  @media (max-width: 480px) {
    font-size: 2.3rem;
  }
`;





const ImageContainer = styled.div`
  width: 50%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  position: relative;
  padding: 2rem 0;
  
  .animate-float {
    animation: ${float} 3s ease-in-out infinite;
  }
  
  .animate-pulse-glow {
    animation: ${pulseGlow} 2s ease-in-out infinite;
  }
  
  img {
    max-width: 100%;
    max-height: 100%;
    width: auto;
    height: auto;
    object-fit: contain;
    filter: drop-shadow(0px 15px 20px rgba(0, 0, 0, 0.15));
  }
  
  @media (max-width: 992px) {
    width: 100%;
    height: auto;
    justify-content: center;
    padding: 1rem 0;
    order: 2; /* Ensure image is below text on mobile */
  }
  
  @media (max-width: 768px) {
    max-height: 250px;
  }
  
  @media (max-width: 480px) {
    max-height: 200px;
  }
`;

const CallToAction = styled.div`
  margin-top: 2rem;
  
  @media (max-width: 768px) {
    margin-top: 0;
    display: flex;
    justify-content: center;
  }
`;

const ServicesButton = styled.button`
  position: relative;
  background: transparent;
  color: #4169e1;
  font-weight: bold;
  text-transform: uppercase;
  border: 1px solid #4169e1;
  padding: 14px 24px; /* Increased padding */
  border-radius: 25px;
  transition: all 0.3s;
  font-size: 1.1rem; /* Added font size */
  letter-spacing: 0.5px; /* Better spacing */
  
  &:hover {
    background-color: #4169e1;
    color: white;
    transform: translateY(-3px);
  }
`;

// RTL version of Services Button with left-positioned arrow
const RtlServicesButton = styled.a`
  position: relative;
  display: inline-block;
  background: transparent;
  color: #4169e1;
  font-weight: bold;
  text-transform: uppercase;
  border: 1px solid #4169e1;
  padding: 14px 24px 14px 56px; /* Extra padding on left for arrow */
  border-radius: 25px;
  transition: all 0.3s;
  font-size: 1.1rem;
  letter-spacing: 0.5px;
  text-decoration: none;
  
  &:hover {
    background-color: #4169e1;
    color: white;
    transform: translateY(-3px);
  }
  
  svg {
    position: absolute;
    left: 20px;
    top: 50%;
    transform: translateY(-50%);
  }
`;

const Hero = ({ id }) => {
  const { t, i18n } = useTranslation();
  const isRtl = i18n.language === 'ar';
  const navigate = useNavigate();
  
  const scrollToServices = () => {
    navigate('/services');
  };
  
  return (
    <GlobalStyle>
    <HeroSection id={id} style={{ minHeight: '80vh' }}>
      <HeroContent>
        <TextContainer>
          <MainHeading>
            {isRtl ? (
              /* Arabic heading format - three lines */
              <>
                {t('home.hero.mainHeading.part1')}<br/>
                <span style={{color: '#4169e1'}}>{t('home.hero.mainHeading.part2')}</span><br/>
                <span style={{color: '#4169e1'}}>{t('home.hero.mainHeading.part3')}</span>
              </>
            ) : (
              /* English heading format - one line */
              <>
                {t('home.hero.mainHeading.part1')} <span className="primary">{t('home.hero.mainHeading.part2')}</span> <span style={{color: '#4169e1'}}>{t('home.hero.mainHeading.part3')}</span>
              </>
            )}
          </MainHeading>
          
          {/* Desktop button only */}
          <div className="desktop-only">
            <CallToAction>
              {isRtl ? (
                <RtlServicesButton onClick={scrollToServices}>
                  <svg 
                    width="20" 
                    height="20" 
                    viewBox="0 0 24 24" 
                    fill="none" 
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path d="M19 12H5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    <path d="M12 5L5 12L12 19" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                  {t('home.hero.cta')}
                </RtlServicesButton>
              ) : (
                <ServicesButton onClick={scrollToServices}>
                  {t('home.hero.cta')}
                  <span style={{ display: 'inline-block', marginLeft: '12px' }}>
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M13 5L20 12L13 19" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      <path d="M20 12H4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </span>
                </ServicesButton>
              )}
            </CallToAction>
          </div>
        </TextContainer>
        
        <ImageContainer>
          {/* Tooth image */}
          <ToothContainer>
            <AnimatedTooth>
              <img
                src="/images/3d-tooth.png"
                alt="3D Dental Crown"
                className="tooth-image"
                style={{ background: 'transparent' }}
              />
            </AnimatedTooth>
          </ToothContainer>
        </ImageContainer>
      </HeroContent>
      
      {/* Mobile button - positioned with absolute positioning for perfect centering */}
      <div className="mobile-tablet-only" style={{ 
        position: 'absolute', 
        left: '50%',
        transform: 'translateX(-50%)',
        bottom: '15%', /* Position from bottom of section */
        width: '280px',
        zIndex: 10
      }}>
        {isRtl ? (
          <RtlServicesButton onClick={scrollToServices} style={{ width: '100%' }}>
            <svg 
              width="20" 
              height="20" 
              viewBox="0 0 24 24" 
              fill="none" 
              xmlns="http://www.w3.org/2000/svg"
            >
              <path d="M19 12H5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M12 5L5 12L12 19" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            {t('home.hero.cta')}
          </RtlServicesButton>
        ) : (
          <ServicesButton onClick={scrollToServices} style={{ width: '100%' }}>
            {t('home.hero.cta')}
            <span style={{ display: 'inline-block', marginLeft: '12px' }}>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M13 5L20 12L13 19" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M20 12H4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </span>
          </ServicesButton>
        )}
      </div>
    </HeroSection>
    </GlobalStyle>
  );
};

export default Hero;
